import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Grid,
    CircularProgress,
    Card,
    CardContent,
    useTheme
} from '@mui/material';
import { toast } from 'react-toastify';
import { fetchSupportEmail, updateSupportEmail } from '../../Redux/Reducers/SettingsReducer';
import usePermissions from '../../hooks/usePermissions';

const MODULE_KEY = 'settings';

const ContactSettings = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dispatch = useDispatch();
    const { hasAction } = usePermissions();
    const { settings, loading } = useSelector((state) => state.settings);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            email: '',
            phone: '',
        },
    });

    useEffect(() => {
        dispatch(fetchSupportEmail())
            .unwrap()
            .then((data) => {
                reset(data);
            })
            .catch((err) => {
                toast.error(err);
            });
    }, [dispatch, reset]);

    const onSubmit = async (data) => {
        try {
            await dispatch(updateSupportEmail(data)).unwrap();
            toast.success(t('settings_updated', 'Settings updated successfully!'));
        } catch (err) {
            toast.error(err);
        }
    };

    return (
        <Box sx={{ p: '36px 24px 24px', maxWidth: '900px' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', lineHeight: '32px', mb: '24px', }} className='main_title'>{t('contact_settings', 'Contact Settings')}</Typography>
            <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(230, 210, 249, 0.2)', borderRadius: '10px' }}>
                <CardContent>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Grid container>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    {...register('email', {
                                        required: t('email_required', 'Email is required'),
                                        pattern: {
                                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                            message: t('invalid_email', 'Invalid email address')
                                        }
                                    })}
                                    error={!!errors.email}
                                    helperText={errors.email?.message}
                                    placeholder={t('support_email', 'Support Email')}
                                    sx={{
                                        mb: '20px',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '999px',
                                            backgroundColor: 'transparent',
                                            color: '#fff',
                                            fontSize: '15px',
                                            '& fieldset': {
                                                borderColor: 'rgba(230, 210, 249, 0.2)',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: 'rgba(230, 210, 249, 0.2)',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: 'rgba(230, 210, 249, 0.2)',
                                                borderWidth: '1px',
                                            },
                                        },
                                        '& .MuiOutlinedInput-input': {
                                            padding: '12px 20px',
                                            color: '#ff',
                                        },
                                        '& input::placeholder': {
                                            color: 'rgba(255,255,255,0.6)',
                                            opacity: 1,
                                        },
                                        '& .MuiFormHelperText-root': {
                                            ml: '20px'
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    {...register('phone', {
                                        required: t('phone_required', 'Phone number is required'),
                                    })}
                                    error={!!errors.phone}
                                    helperText={errors.phone?.message}
                                    placeholder={t('support_phone', 'Support Phone')}
                                    sx={{
                                        mb: '20px',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '999px',
                                            backgroundColor: 'transparent',
                                            color: '#fff',
                                            fontSize: '15px',
                                            '& fieldset': {
                                                borderColor: 'rgba(230, 210, 249, 0.2)',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: 'rgba(230, 210, 249, 0.2)',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: 'rgba(230, 210, 249, 0.2)',
                                                borderWidth: '1px',
                                            },
                                        },
                                        '& .MuiOutlinedInput-input': {
                                            padding: '12px 20px',
                                            color: '#ff',
                                        },
                                        '& input::placeholder': {
                                            color: 'rgba(255,255,255,0.6)',
                                            opacity: 1,
                                        },
                                        '& .MuiFormHelperText-root': {
                                            ml: '20px'
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={loading || !hasAction(MODULE_KEY, 'update')}
                                    sx={{
                                        py: 1.5,
                                        borderRadius: '999px',
                                        fontWeight: '400',
                                        fontSize: '15px',
                                        lineHeight: '24px',
                                        textTransform: 'capitalize',
                                        background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
                                        minWidth: '200px',
                                    }}
                                >
                                    {loading ? <CircularProgress size={24} color="inherit" /> : t('update_settings', 'Update Settings')}
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                </CardContent>
            </Card>
        </Box>
    );
};

export default ContactSettings;
